import { yupResolver } from "@hookform/resolvers/yup";
import { useAppStatus } from "@wexinc/wex-common-web";
import { Button, EnvelopeIcon, Form, TextField, WexLogo } from "@wexinc/wex-ui";
import { useEffect, useRef, useState } from "react";
import useCopSecurityService from "services/security/useCopSecurityService";
import * as yup from "yup";
import useSecurityNavigation from "../useSecurityNavigation";
import BlobSVG from "./BlobSVG";
import mainImage from "./MainImage.png";

interface LoginParams {
  email: string;
}

const schema = yup.object<LoginParams>({
  email: yup.string().email("Must be a valid email.").trim().required("Email is required."),
});

const Login: React.FC = () => {
  const securityService = useCopSecurityService();
  const isSilentlyLoggingIn = useRef(false);
  const loginNavigation = useSecurityNavigation();
  const appStatus = useAppStatus();
  const [enableContinueButton, setEnableContinueButton] = useState(true);

  const originalUrl = securityService.getOriginalUrl();
  const redirectUrl = securityService.getRedirectUrl();

  const isFirefox = navigator.userAgent.indexOf("Firefox") != -1;
  // extra check b/c Chrome user agent includes the phrase "Safari"
  const isSafari = navigator.userAgent.indexOf("Safari") != -1 && navigator.userAgent.indexOf("Chrome") === -1;
  const isFirefoxOrSafari = isFirefox || isSafari;

  async function handleSubmit(loginParams: LoginParams) {
    setEnableContinueButton(false);

    const idp = await securityService.getIdpsOktaIDs(loginParams.email);

    if (idp.identityProviderId === null) {
      loginNavigation.navigateToUsernamePasswordLogin(loginParams.email, redirectUrl, originalUrl);
    } else {
      loginNavigation.navigateToIdpLogin(loginParams.email, idp.identityProviderId, redirectUrl, originalUrl);
    }
  }

  function reestablishSessionIfApplicable() {
    if (
      securityService.securityStore.authStatus === "authenticated" &&
      securityService.securityStore.loggedInUser?.email &&
      securityService.wasLoggedOutExplicitly() === false &&
      isSilentlyLoggingIn.current === false
    ) {
      isSilentlyLoggingIn.current = true;
      appStatus.setAppStatus("reestablishing-session");

      setTimeout(() => {
        securityService.loginViaPreferredIDP(
          securityService.securityStore.loggedInUser!.email!,
          redirectUrl,
          originalUrl,
        );
      }, 50);
    }
  }

  reestablishSessionIfApplicable();

  useEffect(() => {
    reestablishSessionIfApplicable();
    // eslint-disable-next-line
  }, [securityService.securityStore.authStatus, isSilentlyLoggingIn.current]);

  // Annoying to have to do, but in the authorized area of the app, securityService takes care of this.
  useEffect(() => appStatus.setAppStatusAction("reset"), []);

  return (
    <>
      {!isSilentlyLoggingIn.current && (
        <>
          <div className="flex h-full items-center mx-auto">
            <div className="flex items-center mx-auto">
              <section className="flex flex-col w-[400px] ml-[20px]">
                <WexLogo
                  height="40px"
                  width="124px"
                />
                <div className="pb-10 pt-2 text-neutral-dark-60 text-lg-plus font-semibold">
                  Customer Operations Portal
                </div>
                <div className="pb-4 text-neutral-dark-70 font-bold text-xl">Log in</div>
                <Form
                  onSubmit={handleSubmit}
                  options={{ resolver: yupResolver(schema) }}
                >
                  <>
                    <TextField
                      name="email"
                      label="Email"
                      icon={<EnvelopeIcon className="fill-current" />}
                    />
                    <Button
                      id="login-submit"
                      className="w-full !h-14"
                      testId="login-button"
                      type="submit"
                      disabled={!enableContinueButton}
                    >
                      Continue
                    </Button>
                    {isFirefoxOrSafari && (
                      <p className="mt-6 text-neutral-dark-60 text-sm">{`${isFirefox ? "Firefox" : ""}${
                        isSafari ? "Safari" : ""
                      } users must enable ${isFirefox ? "cross-site tracking cookies" : ""}${
                        isSafari ? "cross-site tracking and cookies" : ""
                      } in their browser preferences in order to proceed with login`}</p>
                    )}
                  </>
                </Form>
              </section>
              <section className="relative ml-[110px]">
                <img
                  src={mainImage}
                  alt="Illustration"
                  className="relative z-10"
                />
              </section>
            </div>
          </div>

          <BlobSVG
            width="596"
            height="297"
            viewBox="0 0 596 297"
            className="absolute bottom-0 right-0"
            opacity="0.5"
          />

          <BlobSVG
            width="565"
            height="285"
            viewBox="0 0 565 285"
            className="absolute bottom-0 right-0"
            opacity="1"
          />
        </>
      )}
    </>
  );
};

export default Login;
